import { FC, Fragment, ReactNode, useEffect } from 'react';
import {
  Box,
  List,
  Pagination,
} from '@mui/material';

import { usePagination } from '@cvt/hooks/usePagination';
import { MessageFeedbackView } from '@cvt/components/MessageFeedbackView';

import { useDisciplines } from '../hooks/useDisciplines';

const displayAmount = 10;

interface ViewProps {
  filters?: Disciplines.GetListParams;
  renderItem: (discipline: Disciplines.Discipline) => ReactNode;
  options?: {
    enablePagination?: boolean;
  };
  slots?: {
    after?: ReactNode | ((props: { count: number, disciplines: Disciplines.Discipline[] }) => ReactNode);
  };
}

export const DisciplinesList: FC<ViewProps> = ({ filters = {}, renderItem, options, slots }) => {

  const { totalPages, setCount, page, setPage, limit, offset } = usePagination(filters.limit || displayAmount);

  const { count, disciplines, status } = useDisciplines({
    ...filters,
    offset,
    limit,
  });

  useEffect(() => {
    if (status === 'success') {
      setCount(count);
    }
  }, [status, setCount, count]);

  const renderAfterSlot = () => {
    if (!slots?.after) return null;
    
    if (typeof slots.after === 'function') {
      return slots.after({
        count,
        disciplines,
      });
    }
    
    return slots.after;
  };

  return (
    <Fragment>
      {status === 'error' && (
        <MessageFeedbackView height="100%"/>
      )}
      {status === 'success' && (
        <Fragment>
          {disciplines.length === 0 && (
            <MessageFeedbackView height={400} message="No Disciplines"/>
          )}
          {disciplines.length > 0 && (
            <Fragment>
              <List disablePadding>
                {disciplines.map(discipline => renderItem(discipline))}
              </List>
            </Fragment>
          )}
          {renderAfterSlot()}
          {options?.enablePagination && disciplines.length > 0 && (
            <Box display="flex" width="100%" p={2} mt="auto" justifyContent="center">
              <Pagination
                count={totalPages}
                page={page}
                onChange={(event, value) => setPage(value)}
                showFirstButton={page !== 1}
                showLastButton={page !== totalPages}
              />
            </Box>
          )}
        </Fragment>
      )}
    </Fragment>
  );
};
